import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LogOut, User } from 'lucide-react'
import type { AuthUser } from '@/types/auth'

interface Props {
  user: AuthUser | null
  onLogout: () => void | Promise<void>
  footerSlot?: React.ReactNode
}

const AccountMenu: React.FC<Props> = ({ user, onLogout, footerSlot }) => {
  const navigate = useNavigate()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    if (dropdownOpen) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [dropdownOpen])

  const fullName =
    `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim() || user?.email || 'Usuário'

  // Cliente (inclui "ver como") vê a empresa; operacional vê o tipo de usuário.
  const subtitle =
    user?.views_profile === 'cliente'
      ? user?.view_as_company_name ||
        user?.primary_company_name ||
        user?.user_type_name ||
        'Empresa não atribuída'
      : user?.user_type_name || 'Cargo não informado'

  const profilePicture = (() => {
    const picture = user?.google_picture || user?.microsoft_picture
    if (picture && picture.startsWith('http')) {
      if (picture.includes('googleusercontent.com')) {
        const base = picture.split('?')[0]
        return `${base}?sz=100&d=404`
      }
      return picture
    }
    return null
  })()

  const handleLogout = async () => {
    try {
      await onLogout()
    } catch {
      // ignore
    }
    window.location.href = '/login'
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setDropdownOpen((v) => !v)}
        className="flex items-center gap-3"
        title="Menu da conta"
      >
        <div className="text-right">
          <p className="text-sm font-medium text-body/80 leading-tight">{fullName}</p>
          <p className="text-xs text-muted leading-tight">{subtitle}</p>
        </div>
        {profilePicture ? (
          <img
            src={profilePicture}
            alt={fullName}
            className="w-10 h-10 rounded-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none'
              const sib = e.currentTarget.nextElementSibling as HTMLElement | null
              if (sib) sib.style.display = 'flex'
            }}
          />
        ) : null}
        <div
          className="w-10 h-10 bg-[#2563eb] rounded-full items-center justify-center"
          style={{ display: profilePicture ? 'none' : 'flex' }}
        >
          <User className="w-5 h-5 text-white" />
        </div>
      </button>

      {dropdownOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-surface rounded-[5px] shadow-lg border border-border-subtle z-50">
          <div className="p-3 border-b border-[#e4e9f0] dark:border-border-faint">
            <h3 className="text-sm font-semibold text-[#3d4c60] dark:text-body/80">Minha conta</h3>
          </div>
          <div className="p-2">
            <button
              type="button"
              onClick={() => {
                setDropdownOpen(false)
                navigate('/perfil')
              }}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-[#3d4c60] dark:text-body/80 hover:bg-surface-muted rounded-[5px]"
            >
              <User className="w-4 h-4 text-[#3d4c60] dark:text-body/80" />
              <span className="font-medium">Perfil</span>
            </button>
          </div>
          {footerSlot && <div className="p-2 pt-0">{footerSlot}</div>}
          <div className="border-t border-border-faint" />
          <div className="p-2">
            <button
              type="button"
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-[#3d4c60] dark:text-body/80 hover:bg-surface-muted rounded-[5px]"
            >
              <LogOut className="w-4 h-4 text-[#3d4c60] dark:text-body/80" />
              <span className="font-medium">Sair</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default AccountMenu
